import { useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Star, CheckCircle, PencilSimpleLine } from '@phosphor-icons/react';
import type { AxiosError } from 'axios';
import { errorStatus, extractErrorMessage } from '../../lib/error';
import { useSubmitReview } from '../../hooks/useReviews';

/**
 * Post-stay review flow (TASK-43). Reachable from the booking confirmation /
 * manage screen once the stay is complete. The guest proves the stay with the
 * booking reference (prefilled) + the 4-digit PIN, picks 1–5 stars, and may add
 * a title + note. On success it shows a thank-you (the review lands pending
 * moderation before it appears publicly).
 */
export function WriteReview({ bookingRef, pin }: { bookingRef: string; pin?: string }) {
  const [open, setOpen] = useState(false);
  const [pinInput, setPinInput] = useState(pin ?? '');
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const starRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const submit = useSubmitReview();

  // Radiogroup keyboard nav (WAI-ARIA): arrows move + select the rating and
  // wrap; Home/End jump to the extremes. Roving tabindex keeps a single stop.
  function onStarKeyDown(e: KeyboardEvent<HTMLButtonElement>, n: number) {
    let next: number;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = n >= 5 ? 1 : n + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = n <= 1 ? 5 : n - 1;
    else if (e.key === 'Home') next = 1;
    else if (e.key === 'End') next = 5;
    else return;
    e.preventDefault();
    setRating(next);
    starRefs.current[next - 1]?.focus();
  }

  const status = errorStatus(submit.error);
  const err = submit.isError
    ? status === 404
      ? 'Booking not found or the PIN is incorrect.'
      : status === 409
        ? (submit.error as AxiosError<{ code?: string }>)?.response?.data?.code === 'ALREADY_REVIEWED'
          ? 'You have already reviewed this stay.'
          : 'You can leave a review once your stay is complete.'
        : status === 429
          ? 'Too many attempts — please wait a moment.'
          : extractErrorMessage(submit.error)
    : null;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (rating < 1 || !/^\d{4}$/.test(pinInput)) return;
    submit.mutate({
      bookingReference: bookingRef,
      pin: pinInput,
      rating,
      ...(title.trim() ? { title: title.trim() } : {}),
      ...(body.trim() ? { body: body.trim() } : {}),
    });
  }

  if (submit.isSuccess) {
    return (
      <div className="mt-4 rounded-2xl bg-brand-blue-light px-5 py-6 text-center">
        <CheckCircle className="mx-auto h-10 w-10 text-brand-blue" />
        <h3 className="mt-2 font-bold text-brand-navy">Thanks for your review!</h3>
        <p className="mt-1 text-sm text-gray-600">
          It'll appear on the property page within a few hours, once our team gives it a quick check.
        </p>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-primary-200 px-5 py-4">
        <p className="text-sm text-brand-navy">Enjoyed your stay? Share a review to help other guests.</p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:bg-brand-blue-dark"
        >
          <PencilSimpleLine className="h-4 w-4" /> Write a review
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="mt-4 rounded-2xl border border-gray-200 p-5 text-left">
      <h3 className="text-sm font-bold text-brand-navy">Write a review</h3>

      {/* Star picker */}
      <div className="mt-3">
        <span id="rating-label" className="text-xs font-semibold text-gray-500">Your rating</span>
        <div role="radiogroup" aria-labelledby="rating-label" className="mt-1 flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              ref={(el) => { starRefs.current[n - 1] = el; }}
              type="button"
              role="radio"
              aria-checked={rating === n}
              aria-label={`${n} star${n === 1 ? '' : 's'}`}
              tabIndex={rating === n || (rating === 0 && n === 1) ? 0 : -1}
              onClick={() => setRating(n)}
              onKeyDown={(e) => onStarKeyDown(e, n)}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              className="rounded p-0.5 outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
            >
              <Star
                weight={n <= (hover || rating) ? 'fill' : 'light'}
                className={`h-7 w-7 ${n <= (hover || rating) ? 'text-brand-blue' : 'text-primary-200'}`}
              />
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-xs font-semibold text-gray-500">
          4-digit PIN
          <input
            inputMode="numeric"
            maxLength={4}
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm tracking-[0.3em] text-gray-900 outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
          />
        </label>
        <label className="text-xs font-semibold text-gray-500">
          Title (optional)
          <input
            type="text"
            maxLength={150}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
          />
        </label>
      </div>

      <label className="mt-3 block text-xs font-semibold text-gray-500">
        Your review (optional)
        <textarea
          rows={4}
          maxLength={2000}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="What made your stay memorable?"
          className="mt-1 w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
        />
      </label>

      {err && (
        <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
          {err}
        </p>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-brand-navy"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submit.isPending || rating < 1 || pinInput.length !== 4}
          className="rounded-xl bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:bg-brand-blue-dark disabled:opacity-60"
        >
          {submit.isPending ? 'Submitting…' : 'Submit review'}
        </button>
      </div>
    </form>
  );
}
